import {
  CalendarToday,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  LocationOn,
  School,
  Work
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Container,
  Fade,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';

const SectionContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(10, 0),
  background: 'transparent',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}, transparent)`,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 30% 70%, rgba(0, 173, 181, 0.05) 0%, transparent 50%),
                 radial-gradient(circle at 70% 30%, rgba(238, 238, 238, 0.02) 0%, transparent 50%)`,
    pointerEvents: 'none',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gilroy, sans-serif',
  fontWeight: 800,
  fontSize: 'clamp(2rem, 4vw, 3rem)',
  textAlign: 'center',
  marginBottom: theme.spacing(2),
  background: `linear-gradient(45deg, ${theme.palette.text.primary}, ${theme.palette.primary.main})`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  backgroundClip: 'text',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 0,
    height: 3,
    background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: 2,
    transition: 'width 0.8s ease-in-out',
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: -12,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 80,
    height: 3,
    background: 'rgba(0, 173, 181, 0.1)',
    borderRadius: 2,
  },
  '&.animate::after': {
    width: 80,
  },
}));

const SectionSubtitle = styled(Typography)(({ theme }) => ({
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontSize: 'clamp(1rem, 2vw, 1.2rem)',
  marginBottom: theme.spacing(6),
  maxWidth: 600,
  margin: `0 auto ${theme.spacing(6)}px auto`,
}));

const ContentWrapper = styled(Box)(({ theme }) => ({
  background: 'rgba(34, 40, 49, 0.7)',
  backdropFilter: 'blur(15px)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(5),
  border: '1px solid rgba(0, 173, 181, 0.1)',
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(3),
  },
}));

// Horizontal Timeline Components (Desktop)
const HorizontalTimelineContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  overflowX: 'auto',
  paddingBottom: theme.spacing(2),
  '&::-webkit-scrollbar': {
    height: 8,
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 4,
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.primary.main,
    borderRadius: 4,
  },
}));

const TimelineWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  minWidth: 'fit-content',
  padding: theme.spacing(4, 0),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: 0,
    right: 0,
    height: 3,
    background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: 2,
    transform: 'translateY(-50%)',
    zIndex: 1,
  },
}));

const TimelineItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  minWidth: 350,
  margin: theme.spacing(0, 2),
  zIndex: 2,
}));

// Vertical Timeline Components (Mobile)
const VerticalTimelineContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  padding: theme.spacing(2, 0),
}));

const VerticalTimelineWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '30px',
    width: 3,
    background: `linear-gradient(180deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
    borderRadius: 2,
    zIndex: 1,
  },
}));

const VerticalTimelineItem = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(2),
  zIndex: 2,
}));

const TimelineIcon = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.background.default,
  fontSize: '1.8rem',
  boxShadow: `0 0 20px rgba(0, 173, 181, 0.4)`,
  marginBottom: theme.spacing(2),
  transition: 'all 0.3s ease',
  flexShrink: 0,
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: `0 0 30px rgba(0, 173, 181, 0.6)`,
  },
}));

const VerticalTimelineIcon = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.background.default,
  fontSize: '1.8rem',
  boxShadow: `0 0 20px rgba(0, 173, 181, 0.4)`,
  transition: 'all 0.3s ease',
  flexShrink: 0,
  position: 'relative',
  zIndex: 2,
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: `0 0 30px rgba(0, 173, 181, 0.6)`,
  },
}));

const TimelineCard = styled(Card)(({ theme }) => ({
  background: 'rgba(57, 62, 70, 0.9)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(0, 173, 181, 0.2)',
  borderRadius: theme.spacing(2),
  width: '100%',
  maxWidth: 320,
  minWidth: 320,
  height: 260,
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    border: '1px solid rgba(0, 173, 181, 0.4)',
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const VerticalTimelineCard = styled(Card)(({ theme }) => ({
  background: 'rgba(57, 62, 70, 0.9)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(0, 173, 181, 0.2)',
  borderRadius: theme.spacing(2),
  width: '100%',
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateX(8px)',
    border: '1px solid rgba(0, 173, 181, 0.4)',
    boxShadow: '0 15px 40px rgba(0, 0, 0, 0.4)',
  },
}));

const CompanyName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Gilroy, sans-serif',
  fontWeight: 700,
  fontSize: '1rem',
  color: theme.palette.primary.main,
  marginBottom: theme.spacing(0.125),
}));

const Position = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '0.9rem',
  color: theme.palette.text.primary,
  marginBottom: theme.spacing(0.5),
}));

const Duration = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(0.25),
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

const Location = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(0.75),
  color: theme.palette.text.secondary,
  fontSize: '0.75rem',
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  marginBottom: theme.spacing(0.75),
  paddingRight: theme.spacing(1),
  minHeight: 0,
  maxHeight: '60px',
  '&::-webkit-scrollbar': {
    width: 4,
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0, 0, 0, 0.1)',
    borderRadius: 2,
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 173, 181, 0.5)',
    borderRadius: 2,
  },
}));

const VerticalScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'visible',
  marginBottom: theme.spacing(0.75),
  paddingRight: theme.spacing(1),
  minHeight: 0,
}));

const Description = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  lineHeight: 1.3,
  fontSize: '0.8rem',
  marginBottom: 0,
}));

const NavigationButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  background: 'rgba(0, 173, 181, 0.1)',
  color: theme.palette.primary.main,
  border: '1px solid rgba(0, 173, 181, 0.3)',
  zIndex: 3,
  '&:hover': {
    background: 'rgba(0, 173, 181, 0.2)',
  },
  '&:disabled': {
    opacity: 0.3,
  },
}));

const LeftNavButton = styled(NavigationButton)(({ theme }) => ({
  left: theme.spacing(1),
}));

const RightNavButton = styled(NavigationButton)(({ theme }) => ({
  right: theme.spacing(1),
}));

// Combined timeline data sorted by start date (most recent first)
const timelineData = [
  {
    id: 1,
    company: 'Freespace',
    position: 'Senior Software Engineer',
    duration: 'Jun 2023 – Present',
    location: 'Mumbai, India',
    description: 'Leading architecture, micro frontends, and secure CI/CD implementations.',
    type: 'work',
    technologies: ['React', 'Node.js', 'AWS', 'TypeScript', 'Microservices']
  },
  {
    id: 2,
    company: 'Freespace',
    position: 'Software Engineer',
    duration: 'Jun 2021 – Jun 2023',
    location: 'Mumbai, India',
    description: 'Contributed to cross-system integrations and cloud-native development.',
    type: 'work',
    technologies: ['React', 'Node.js', 'AWS', 'Spring Boot', 'MySQL']
  },
  {
    id: 3,
    company: 'Decabits',
    position: 'Software Engineer',
    duration: 'Feb 2021 – Jun 2021',
    location: 'Delhi, India',
    description: 'Integrated Facebook sharing and optimized React Native app.',
    type: 'work',
    technologies: ['React Native', 'Node.js', 'Facebook API', 'MongoDB']
  },
  {
    id: 4,
    company: 'TIKAJ',
    position: 'Software Engineer',
    duration: 'Jun 2020 – Sep 2020',
    location: 'Gurgaon, India',
    description: 'Developed Node.js package for AWS S3 and built Vue.js UI component library.',
    type: 'work',
    technologies: ['Vue.js', 'Node.js', 'AWS S3', 'JavaScript']
  },
  {
    id: 5,
    company: 'TIKAJ',
    position: 'Software Engineer Intern',
    duration: 'Oct 2019 – Jun 2020',
    location: 'Gurgaon, India',
    description: 'Built real estate website and improved UX on company site using CodeIgniter.',
    type: 'work',
    technologies: ['CodeIgniter', 'PHP', 'MySQL', 'JavaScript']
  },
  {
    id: 6,
    company: 'Lovely Professional University',
    position: 'B.Tech in Computer Science & Engineering',
    duration: '2016 – 2020',
    location: 'Phagwara, India',
    description: 'Graduated with a focus on full stack development and cloud architecture.',
    type: 'education',
    technologies: ['Data Structures', 'Algorithms', 'Database Systems', 'Software Engineering']
  }
];

const ExperienceSection: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [animateTitle, setAnimateTitle] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Delay the title animation slightly for better effect
          setTimeout(() => setAnimateTitle(true), 300);
        }
      },
      { threshold: 0.2 }
    );

    const element = document.getElementById('experience');
    if (element) {
      observer.observe(element);
    }

    return () => observer.disconnect();
  }, []);

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      return () => container.removeEventListener('scroll', checkScrollButtons);
    }
  }, []);

  const handleScroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const scrollAmount = 300;
      const currentScroll = scrollContainerRef.current.scrollLeft;
      const newScroll = direction === 'left' 
        ? currentScroll - scrollAmount 
        : currentScroll + scrollAmount;
      
      scrollContainerRef.current.scrollTo({
        left: newScroll,
        behavior: 'smooth'
      });
    }
  };

  // Render horizontal timeline for desktop
  const renderHorizontalTimeline = () => (
    <Box position="relative">
      <HorizontalTimelineContainer ref={scrollContainerRef}>
        <TimelineWrapper>
          {timelineData.map((item, index) => (
            <Fade
              key={item.id}
              in={isVisible}
              timeout={1000 + index * 200}
            >
              <TimelineItem>
                <TimelineIcon>
                  {item.type === 'work' ? <Work /> : <School />}
                </TimelineIcon>
                
                <TimelineCard>
                  <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <CompanyName variant="h6">
                      {item.company}
                    </CompanyName>
                    
                    <Position variant="h6">
                      {item.position}
                    </Position>
                    
                    <Duration>
                      <CalendarToday sx={{ fontSize: '1rem' }} />
                      <Typography variant="body2">{item.duration}</Typography>
                    </Duration>
                    
                    <Location>
                      <LocationOn sx={{ fontSize: '1rem' }} />
                      <Typography variant="body2">{item.location}</Typography>
                    </Location>
                    
                    <ScrollableContent>
                      <Description variant="body2">
                        {item.description}
                      </Description>
                    </ScrollableContent>
                    
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {item.technologies.map((tech, techIndex) => (
                        <Chip
                          key={techIndex}
                          label={tech}
                          size="small"
                          sx={{
                            background: 'rgba(0, 173, 181, 0.1)',
                            color: 'primary.main',
                            border: '1px solid rgba(0, 173, 181, 0.3)',
                            fontSize: '0.7rem',
                            height: 20,
                            '&:hover': {
                              background: 'rgba(0, 173, 181, 0.2)',
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </TimelineCard>
              </TimelineItem>
            </Fade>
          ))}
        </TimelineWrapper>
      </HorizontalTimelineContainer>

      <LeftNavButton
        onClick={() => handleScroll('left')}
        disabled={!canScrollLeft}
      >
        <KeyboardArrowLeft />
      </LeftNavButton>
      
      <RightNavButton
        onClick={() => handleScroll('right')}
        disabled={!canScrollRight}
      >
        <KeyboardArrowRight />
      </RightNavButton>
    </Box>
  );

  // Render vertical timeline for mobile
  const renderVerticalTimeline = () => (
    <VerticalTimelineContainer>
      <VerticalTimelineWrapper>
        {timelineData.map((item, index) => (
          <Fade
            key={item.id}
            in={isVisible}
            timeout={1000 + index * 200}
          >
            <VerticalTimelineItem>
              <VerticalTimelineIcon>
                {item.type === 'work' ? <Work /> : <School />}
              </VerticalTimelineIcon>
              
              <VerticalTimelineCard>
                <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CompanyName variant="h6">
                    {item.company}
                  </CompanyName>
                  
                  <Position variant="h6">
                    {item.position}
                  </Position>
                  
                  <Duration>
                    <CalendarToday sx={{ fontSize: '1rem' }} />
                    <Typography variant="body2">{item.duration}</Typography>
                  </Duration>
                  
                  <Location>
                    <LocationOn sx={{ fontSize: '1rem' }} />
                    <Typography variant="body2">{item.location}</Typography>
                  </Location>
                  
                  <VerticalScrollableContent>
                    <Description variant="body2">
                      {item.description}
                    </Description>
                  </VerticalScrollableContent>
                  
                  <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 1 }}>
                    {item.technologies.map((tech, techIndex) => (
                      <Chip
                        key={techIndex}
                        label={tech}
                        size="small"
                        sx={{
                          background: 'rgba(0, 173, 181, 0.1)',
                          color: 'primary.main',
                          border: '1px solid rgba(0, 173, 181, 0.3)',
                          fontSize: '0.7rem',
                          height: 20,
                          '&:hover': {
                            background: 'rgba(0, 173, 181, 0.2)',
                          },
                        }}
                      />
                    ))}
                  </Stack>
                </CardContent>
              </VerticalTimelineCard>
            </VerticalTimelineItem>
          </Fade>
        ))}
      </VerticalTimelineWrapper>
    </VerticalTimelineContainer>
  );

  return (
    <SectionContainer id="experience">
      <Container maxWidth="lg">
        <ContentWrapper>
          <Fade in={isVisible} timeout={800}>
            <Box>
              <SectionTitle variant="h2" className={animateTitle ? 'animate' : ''} sx={{ 
                  fontSize: { xs: '2.25rem', md: '3rem' }, 
                  fontWeight: 'bold', 
                  mb: 4,
                }}>
                Professional Journey
              </SectionTitle>
              <SectionSubtitle variant="subtitle1" sx={{textAlign: 'center',margin:'0 auto'}}>
                Journey Through Code & Clouds
              </SectionSubtitle>
            </Box>
          </Fade>
          <Box mt={2} />
          {isMobile ? renderVerticalTimeline() : renderHorizontalTimeline()}
        </ContentWrapper>
      </Container>
    </SectionContainer>
  );
};

export default ExperienceSection; 